import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { parse } from '@fint/money';
import type { MonthsResponse, ReportDimension } from '@fint/validation';
import { api, qs } from '@/lib/api';
import type { Period } from '@/lib/dates';
import { useActiveOrg } from '@/stores/auth';
import { useBaseCurrency } from '@/hooks/useOrgSettings';
import { Card, CardContent, CardHeader, CardTitle, ErrorState, StackedBars, changeText, moneyText } from '@/features/common/ui';
import type { Basis } from '@/features/dashboard/shared';
import { drillForMonth } from './drill';

/**
 * Spend (or income) month by month, one colour per bucket, on its own axis.
 *
 * ── WHY ITS OWN CHART ──
 * On one axis with income, a healthy business's expense bars are two pixels
 * tall - the audit's finding F4. Here the axis is the expense's own scale,
 * so a month that was ₹40,000 is readable beside one that was ₹3 lakh, and
 * a category climbing three months in a row is visible before anyone reads
 * a number. The colours are the categories' own, so the legend matches the
 * chips on the Expenses list.
 *
 * ── WHERE IT STOPS ──
 * The API stops the series at the current month. A window that runs to the
 * end of the financial year is not six months of nothing; the caption says
 * where the picture ends and why.
 */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const monthName = (ym: string) => {
  const [year, month] = ym.split('-').map(Number);
  return `${MONTHS[(month ?? 1) - 1]} ${year}`;
};

export function MonthsChart({
  side,
  by = side === 'income' ? 'item' : 'category',
  period,
  basis,
  title,
  top = 6,
  className,
}: {
  side: 'income' | 'expense';
  by?: ReportDimension;
  period: Period;
  basis: Basis;
  title: string;
  top?: number;
  className?: string;
}) {
  const org = useActiveOrg();
  const currency = useBaseCurrency();
  const navigate = useNavigate();
  const query = useQuery({
    queryKey: ['report-months', org?.id, side, by, period.from, period.to, basis, top],
    enabled: !!org?.id,
    queryFn: () => api.get<MonthsResponse>(`/reports/months${qs({ side, by, from: period.from, to: period.to, basis, top })}`),
  });
  const data = query.data;
  const minor = (amount: string) => parse(amount, currency).minor;

  const series = data
    ? data.buckets.map((bucket, b) => ({
        id: bucket.key,
        name: bucket.name,
        colour: bucket.colour,
        values: data.months.map((_, m) => minor(data.values[m]?.[b] ?? '0')),
      }))
    : [];
  const totals = data ? data.totals.map(minor) : [];
  const yearTotals = data ? data.yearTotals.map(minor) : [];

  const extraRows = (i: number) => {
    if (!data) return [];
    const rows = [];
    const now = totals[i] ?? 0;
    if (i > 0) {
      const before = totals[i - 1] ?? 0;
      rows.push({ key: 'prev', label: `vs ${monthName(data.months[i - 1] ?? '')}`, value: changeText(now, before, currency) });
    }
    const lastYear = yearTotals[i] ?? 0;
    const ym = data.months[i] ?? '';
    rows.push({ key: 'year', label: `vs ${monthName(shiftYear(ym))}`, value: changeText(now, lastYear, currency) });
    const count = data.counts[i] ?? 0;
    rows.push({ key: 'count', label: 'Behind it', value: `${count} ${side === 'income' ? (count === 1 ? 'invoice' : 'invoices') : count === 1 ? 'expense' : 'expenses'}` });
    rows.push({ key: 'click', label: 'Click', value: 'opens the month' });
    return rows;
  };

  return (
    <Card className={className} data-testid={`months-${side}`}>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {query.isError ? <ErrorState error={query.error} onRetry={() => { void query.refetch(); }} /> : null}
        {query.isPending ? <div className="skeleton h-64 rounded-lg" aria-busy="true" /> : null}
        {data ? (
          <>
            <StackedBars
              months={data.months}
              groups={[{ id: side, name: side === 'income' ? 'Money in' : 'Money out', short: '', series }]}
              line={{ name: 'Total', colour: 'foreground', values: totals }}
              currency={currency}
              title={title}
              summary={`${side === 'income' ? 'Money in' : 'Money out'} by month, ${data.buckets.length} ${data.buckets.length === 1 ? 'bucket' : 'buckets'}, ${moneyText(totals.reduce((a, b) => a + b, 0), currency)} ${currency} over ${data.months.length} ${data.months.length === 1 ? 'month' : 'months'}`}
              empty={{
                title: side === 'income' ? `Nothing came in during ${period.label}` : `Nothing was spent in ${period.label}`,
                description: side === 'income' ? 'An invoice or a categorised deposit dated in this period will show here.' : 'An expense dated in this period will show here.',
              }}
              extraRows={extraRows}
              onSelect={(i) => {
                const ym = data.months[i];
                if (ym) navigate(drillForMonth(side, ym, basis).href);
              }}
              groupLegend={false}
              height={240}
            />
            {data.endsAt ? (
              <p className="mt-3 text-xs text-muted-foreground" data-testid={`months-${side}-ends`}>
                Ends at {monthName(data.endsAt)}; the rest of {period.label} has not happened yet.
              </p>
            ) : null}
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}

function shiftYear(ym: string): string {
  const [y, m] = ym.split('-');
  return `${Number(y) - 1}-${m}`;
}
